import { UserAccessInfoDTO } from 'api-models'
import { AppButton, Radio, Tooltip } from '@clickguard/design-system'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { useStores } from 'hooks'
import { ChangeEvent, useState } from 'react'
import { RiQuestionLine } from 'react-icons/ri'
import { useUiStore } from 'store'

export const ManagerMember = (props: { row: UserAccessInfoDTO }) => {
	const { row } = props
	const {
		accountStore,
		userStore: { user }
	} = useStores()
	const { user_name, user_id } = row
	const setModalState = useUiStore((s) => s.setModalState)
	const setModalContent = useUiStore((s) => s.setModalContent)

	const [roleChange, setRoleChange] = useState(false)

	const { deleteAccountUser, account, userAccessLevel, isOwnerAccess } =
		accountStore

	const renderDeleteMemberModal = () => {
		setModalContent({
			title: 'Delete User',
			description: `Are you sure you want to delete ${
				user_name ? user_name : 'this user'
			}?`,
			type: ModalTypes.DANGER,
			showIcon: false,
			confirmLabel: 'Delete',
			confirm: () => confirmDeleteMember()
		})
	}

	const renderManageMemberModal = () => {
		setRoleChange(!roleChange)
		setModalContent({
			title: 'Manage Member',
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			body: <ModalBody row={row} deleteModal={renderDeleteMemberModal} />
		})
	}

	const confirmDeleteMember = () => {
		deleteAccountUser(account!.id, user_id)
		setModalState(false)
	}

	const disabledTooltip =
		userAccessLevel(user!.id) === 'READ'
			? 'Analysts don’t have permission for this action'
			: userAccessLevel(user!.id) === 'WRITE'
			? 'Manager don’t have permission for this action'
			: undefined

	const button = (
		<AppButton
			disabled={!isOwnerAccess(user!.id)}
			variant="secondary"
			size="md"
			label="Manage"
			className="!border-t-card-border !px-4 !text-t-base-blue"
			onClick={() => renderManageMemberModal()}
		/>
	)

	return disabledTooltip ? (
		<Tooltip content={disabledTooltip} placement="top" portal>
			{button}
		</Tooltip>
	) : (
		button
	)
}

const ModalBody = (props: {
	row: UserAccessInfoDTO
	deleteModal: () => void
}) => {
	const { row, deleteModal } = props
	const { access_level, user_id } = row

	const {
		accountStore,
		userStore: { user }
	} = useStores()
	const setModalState = useUiStore((s) => s.setModalState)
	const { updateAccountUser, account, isOwnerAccess } = accountStore

	const [role, setRole] = useState<any>(access_level || 'READ')

	const handleRemoveButtonClick = () => {
		setModalState(false)
		deleteModal()
	}

	const handleRoleChange = (
		e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>
	) => {
		const { value } = e.target
		setRole(value)
	}

	const confirmUpdateMember = () => {
		const payload = {
			access_level: role
		}
		updateAccountUser(account!.id, user_id, payload)
		setModalState(false)
	}

	return (
		<>
			<div className="my-4 w-full">
				<div className="text-base text-t-base-blue font-medium">Name</div>
				<div className="text-lg text-t-base-blue font-medium">
					{row.user_name}
				</div>
			</div>
			<div className="flex flex-col gap-2 w-full">
				<div className="text-lg font-medium">Team role</div>
				<Radio
					checked={role === 'WRITE'}
					label="Manager"
					name="team-role"
					value={'WRITE'}
					onChange={handleRoleChange}
				/>
				<Radio
					checked={role === 'READ'}
					label="Analyst"
					name="team-role"
					value={'READ'}
					onChange={handleRoleChange}
				/>

				{isOwnerAccess(user!.id) && (
					<>
						<div className="text-lg font-medium text-gray-700 flex items-center gap-1">
							Transfer ownership
							<Tooltip
								content="Transferring ownership to another member will make them a sole workspace owner, and your role will be reduced to manager."
								position="top"
							>
								<RiQuestionLine className="text-t-gray-hold h-4 w-4" />
							</Tooltip>
						</div>
						<Radio
							checked={role === 'OWNER'}
							label="Owner"
							name="team-role"
							value={'OWNER'}
							onChange={handleRoleChange}
						/>
					</>
				)}
			</div>
			<div className="grid grid-cols-2 items-center gap-6 mt-5">
				<AppButton
					variant="secondary"
					size="lg"
					fullWidth
					label="Remove"
					onClick={() => handleRemoveButtonClick()}
				/>
				<AppButton
					variant="primary"
					size="lg"
					fullWidth
					label="Save"
					onClick={() => confirmUpdateMember()}
				/>
			</div>
		</>
	)
}
